/**
 * CCTooltip -- Control Center field tooltip.
 *
 * A rich tooltip designed for the Control Center's mosaic grid controls.
 * Shows the full field label (with optional server badge) and description
 * in a structured layout, mirroring the truncated inline content.
 *
 * Wraps the entire label+description area so a single hover reveals
 * everything that may be clipped by column overflow.
 */
import type { ReactNode } from "react";
import { useId, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { cn } from "../../utils/cn";

export type CCTooltipProps = {
  /** Field label text */
  label: string;
  /** Field description text */
  description: string;
  /** Optional server badge (e.g. "emby", "jellyfin") */
  serverOnly?: string | null;
  /** Trigger element(s) */
  children: ReactNode;
  /** Additional class on the trigger wrapper */
  className?: string;
  /** Tooltip placement */
  side?: "top" | "bottom";
  /** Hover delay in ms */
  delay?: number;
};

export const CCTooltip = ({
  label,
  description,
  serverOnly,
  children,
  className,
  side = "top",
  delay = 200,
}: CCTooltipProps) => {
  const [open, setOpen] = useState(false);
  const timeoutRef = useRef<number | null>(null);
  const tooltipId = useId();
  const triggerRef = useRef<HTMLDivElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const [position, setPosition] = useState<{ top: number; left: number }>({ top: 0, left: 0 });

  const clearTimer = () => {
    if (timeoutRef.current) {
      window.clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  const handleOpen = () => {
    clearTimer();
    timeoutRef.current = window.setTimeout(() => setOpen(true), delay);
  };

  const handleClose = () => {
    clearTimer();
    setOpen(false);
  };

  useLayoutEffect(() => {
    if (!open) return;

    const calculatePosition = () => {
      const triggerEl = triggerRef.current;
      const panelEl = panelRef.current;
      if (!triggerEl || !panelEl) return;

      const triggerRect = triggerEl.getBoundingClientRect();
      const panelRect = panelEl.getBoundingClientRect();
      const offset = 8;
      const margin = 8;

      let top: number;
      if (side === "bottom") {
        top = triggerRect.bottom + offset;
      } else {
        top = triggerRect.top - offset - panelRect.height;
      }

      // Center horizontally on trigger, clamped to viewport
      let left = triggerRect.left + triggerRect.width / 2 - panelRect.width / 2;

      top = Math.max(margin, Math.min(top, window.innerHeight - panelRect.height - margin));
      left = Math.max(margin, Math.min(left, window.innerWidth - panelRect.width - margin));

      setPosition({ top, left });
    };

    calculatePosition();
    window.addEventListener("resize", calculatePosition);
    window.addEventListener("scroll", calculatePosition, true);
    return () => {
      window.removeEventListener("resize", calculatePosition);
      window.removeEventListener("scroll", calculatePosition, true);
    };
  }, [open, side]);

  return (
    <div
      ref={triggerRef}
      className={cn("min-w-0 overflow-hidden", className)}
      onMouseEnter={handleOpen}
      onMouseLeave={handleClose}
      aria-describedby={open ? tooltipId : undefined}
    >
      {children}
      {open &&
        createPortal(
          <div
            id={tooltipId}
            ref={panelRef}
            role="tooltip"
            style={{ position: "fixed", top: position.top, left: position.left }}
            className="pointer-events-none z-[9999] max-w-xs rounded-lg border border-border/60 bg-card/95 px-3 py-2 shadow-soft animate-in fade-in-0 zoom-in-95 duration-100"
          >
            <div className="flex items-center gap-1.5">
              <span className="text-xs font-medium text-foreground">{label}</span>
              {serverOnly && (
                <span
                  className={cn(
                    "inline-flex items-center rounded-md border px-1.5 py-[1px] text-[10px] leading-tight font-medium capitalize",
                    serverOnly === "emby"
                      ? "bg-emerald-500/15 text-emerald-500 border-emerald-500/30"
                      : "bg-muted/40 text-muted-foreground border-border/40",
                  )}
                >
                  {serverOnly}
                </span>
              )}
            </div>
            <p className="text-[11px] text-muted-foreground mt-0.5 leading-relaxed">{description}</p>
          </div>,
          document.body,
        )}
    </div>
  );
};
